import {
  Alternativnamn,
  AndamalPut,
  SkyddEnskiltPut,
  SkyddOmradePut,
  SkyddPut,
  SkyddSammansattPut,
  Skyddsbestammelse
} from 'models';

import { FotoVyEnum, HandelseTypEnum, RitningVyEnum, RitningstypEnum, SkyddstypEnum } from 'enums';

import { GeoJSONFeatureCollection } from 'ol/format/GeoJSON';

import { parseDateString } from 'utilities/dateUtilities';
import * as Yup from 'yup';

const CommonHandelseFormSchema = {
  handelsetyp: Yup.string(),
  kommentar: Yup.string().nullable().max(40000, 'Kommentarens längd får vara max 40000 tecken'),
  datering_kand: Yup.boolean(),
  datering_from: Yup.number()
    .nullable()
    .typeError('Årtal måste anges med siffror')
    .test('Framtida årtal kan inte användas', 'Framtida årtal kan inte användas', (value) =>
      value ? !isNaN(Number(value)) && Number(value) <= new Date().getFullYear() : true
    ),
  datering_tom: Yup.number()
    .nullable()
    .typeError('Årtal måste anges med siffror')
    .test('Osäkerhetsintervall måste ha två årtal', 'Osäkerhetsintervall måste ha två årtal', (value, context) =>
      value ? context.parent.datering_from !== undefined : true
    )
    .min(Yup.ref('datering_from'), 'Slutår måste vara senare än startår')
    .test('Framtida årtal kan inte användas', 'Framtida årtal kan inte användas', (value) =>
      value ? !isNaN(Number(value)) && Number(value) <= new Date().getFullYear() : true
    ),
  upphovsman: Yup.array().of(
    Yup.object().shape({
      id: Yup.string(),
      roller: Yup.array().of(Yup.string())
    })
  )
};

export const HandelseFormSchema = Yup.object().shape({
  ...CommonHandelseFormSchema
});

export const StrictHandelseFormSchema = Yup.object().shape({
  ...CommonHandelseFormSchema,
  handelsetyp: Yup.string().required('Händelsetyp måste anges'),
  datering_from: Yup.number()
    .typeError('Årtal måste anges med siffror')
    .test('Framtida årtal kan inte användas', 'Framtida årtal kan inte användas', (value) =>
      value ? !isNaN(Number(value)) && Number(value) <= new Date().getFullYear() : true
    )
    .test('Årtal måste anges', 'Årtal måste anges', (value, context) => {
      return !value ? context.parent.datering_kand === false : true;
    })
});

const CommonFormSchema = {
  anteckning: Yup.string(),
  syfte: Yup.string(),
  namn: Yup.string().max(255, 'Namnets längd får vara max 255 tecken').nullable(),
  ursprungligt_primar_andamal: Yup.object().nullable(),
  ursprungligt_sekundar_andamal: Yup.array().nullable(),
  skydd: Yup.object({
    motivering: Yup.string().nullable()
  }),
  geometri: Yup.object(),
  lokal_klassificering: Yup.string().nullable().max(10, 'Max 10 tecken'),
  referenser: Yup.string().nullable(),
  historik: Yup.string().nullable(),
  beskrivning: Yup.string().nullable(),
  handelser: Yup.array().of(HandelseFormSchema),
  karaktarsdrag: Yup.string()
};

export const EnskiltFormSchema = Yup.object().shape({
  ...CommonFormSchema,
  egenskaper: Yup.array()
});

export const OmradeFormSchema = Yup.object().shape({
  ...CommonFormSchema
});

const SammansattFormSchema = Yup.object().shape({
  ...CommonFormSchema,
  ursprungligt_primar_andamal: Yup.object().required('Ursprungligt huvudändamål måste anges'),
  namn: Yup.string().max(255, 'Namnets längd får vara max 255 tecken').trim().required('Namn måste anges')
});

export const PubliceringEnskiltFormSchema = Yup.object().shape({
  ...CommonFormSchema,
  ursprungligt_primar_andamal: Yup.object().required('Ursprungligt huvudändamål måste anges'),
  handelser: Yup.array().of(StrictHandelseFormSchema),
  namn: Yup.string()
    .max(255, 'Namnets längd får vara max 255 tecken')
    .when('alternativnamn', {
      is: (alternativnamn: Array<Alternativnamn>, namn: string) =>
        (namn === undefined || namn === '') && alternativnamn.length > 0,
      then: (schema) => schema.required('Ange ett namn, eller ta bort alternativnamn')
    })
});

export const PubliceraOmradeFormSchema = Yup.object().shape({
  ...CommonFormSchema,
  ursprungligt_primar_andamal: Yup.object().required('Ursprungligt huvudändamål måste anges'),
  namn: Yup.string().max(255, 'Namnets längd får vara max 255 tecken').trim()
  // Notera att det enda sättet att lägga till händelse på område är via modal som redan tillämpar ett strikt schema.
});

// Statliga byggnadsminnen och Byggnadsminnen ska ha minst 1 skyddsbestämmelse.
// Anmälningsplikt kan ha 0-* skyddsbestämmelser.
// Hävda skydd har alltid 0 (noll) skyddsbestämmelser).
export const getSammansattFormSchemaBySkyddstyp = ({
  skyddstyp,
  havt
}: {
  skyddstyp: SkyddstypEnum;
  havt: boolean;
}) => {
  if (havt) return SammansattFormSchema.concat(SammansattHavtSkyddsbestammelserSchema);

  if (skyddstyp === SkyddstypEnum.BYGGNADSMINNE || skyddstyp === SkyddstypEnum.STATLIGT_BYGGNADSMINNE) {
    return SammansattFormSchema.concat(SammansattSBMorBMSkyddsbestammelserSchema);
  } else if (skyddstyp === SkyddstypEnum.ANMALNINGSPLIKT) {
    return SammansattFormSchema.concat(SammansattAnmalningspliktSkyddsbestammelserSchema);
  } else {
    throw new Error(
      `Valideringsschema för sammansatt objekt kunde inte avgöras för skyddstyp: ${skyddstyp}. Hävt: ${havt}`
    );
  }
};

const SkyddsbestammelseSchema = Yup.object().shape({
  attribut_id: Yup.string(),
  rubrik: Yup.string().required('Rubrik måste anges').max(255, 'Rubrikens längd får vara max 255 tecken'),
  skyddsbestammelse: Yup.string()
});

const SammansattSBMorBMSkyddsbestammelserSchema = Yup.object().shape({
  skyddsbestammelser: Yup.array()
    .of(SkyddsbestammelseSchema)
    .required()
    .min(1, 'Minst en skyddsbestämmelse måste anges')
});

const SammansattHavtSkyddsbestammelserSchema = Yup.object().shape({
  skyddsbestammelser: Yup.array()
    .of(SkyddsbestammelseSchema)
    .required()
    .max(0, 'Skyddsbestämmelser för hävda skydd får inte förekomma')
});

const SammansattAnmalningspliktSkyddsbestammelserSchema = Yup.object().shape({
  skyddsbestammelser: Yup.array().of(SkyddsbestammelseSchema)
});

export const FotografiFormSchema = Yup.object().shape({
  bildfil: Yup.mixed<File>()
    .required('Du måste välja en fil')
    .test('size', 'Bildens storlek får vara max 15 MB', (value: File) => value.size <= 15 * 1024 * 1024),
  titel: Yup.string().max(140, 'Titelns längd får vara max 140 tecken').trim().required('Titel måste anges'),
  bildtext: Yup.string().max(255, 'Bildtextens längd får vara max 255 tecken').trim(),
  vy: Yup.string().required('Vy måste anges'),
  licens: Yup.number().required('Licens måste anges'),
  upphovsmanFornamn: Yup.string()
    .max(50, 'Max 50 tecken')
    .when('licens', {
      is: (value: number) => [3, 4].includes(value),
      then: (schema) => schema.required('Fotografens förnamn måste anges')
    }),
  upphovsmanEfternamn: Yup.string()
    .max(50, 'Max 50 tecken')
    .when('licens', {
      is: (value: number) => [3, 4].includes(value),
      then: (schema) => schema.required('Fotografens efternamn måste anges')
    }),
  tillkomstdatum: Yup.date()
    .transform(parseDateString)
    .typeError('Vänligen ange ett giltigt datum')
    .required('Datum måste anges')
    .max(new Date(new Date().setHours(23, 59, 59, 0)), 'Datum får inte vara efter dagens datum')
});

export const SituationsplanFormSchema = Yup.object().shape({
  bildfil: Yup.mixed<File>()
    .required('Du måste välja en fil')
    .test('size', 'Bildens storlek får vara max 15 MB', (value: File) => value.size <= 15 * 1024 * 1024),
  titel: Yup.string().max(140, 'Titelns längd får vara max 140 tecken').trim().required('Titel måste anges'),
  motivbeskrivning: Yup.string().max(900, 'Bildtextens längd får vara max 900 tecken').trim(),
  vy: Yup.string().required('Vy måste anges'),
  tillkomstdatum: Yup.date()
    .transform(parseDateString)
    .typeError('Vänligen ange ett giltigt datum')
    .required('Datum måste anges')
    .max(new Date(new Date().setHours(23, 59, 59, 0)), 'Datum får inte vara efter dagens datum'),
  godkand_spridning: Yup.boolean().isTrue('Spridningstillstånd måste godkännas')
});

export interface HandelseFormValues {
  attribut_id: string;
  handelsetyp: HandelseTypEnum;
  kommentar: string;
  datering_kand: string;
  datering_from: string;
  datering_tom: string;
  upphovsman: Array<{ id: string; roller: Array<string> }>;
}

interface CommonFormValues {
  namn: string;
  alternativnamn: Array<Alternativnamn>;
  beskrivning: string;
  historik: string;
  referenser: string;
  karaktarsdrag: string;
  lokal_klassificering: string;
  ursprungligt_primar_andamal: AndamalPut | null;
  ursprungligt_sekundar_andamal: Array<AndamalPut>;
  anteckning: string;
  skydd: SkyddPut;
  handelser: Array<HandelseFormValues>;
  syfte: string;
  geometri: GeoJSONFeatureCollection;
}

export interface OmradeFormValues extends CommonFormValues {
  skydd: SkyddOmradePut;
}

export interface SammansattFormValues extends CommonFormValues {
  skydd: SkyddSammansattPut;
  skyddsbestammelser: Array<Skyddsbestammelse>;
}

export interface EnskiltFormValues extends CommonFormValues {
  egenskaper: Array<number>;
  skydd: SkyddEnskiltPut;
}

export interface BildPostFormValues {
  titel: string;
  bildtext: string;
  vy: FotoVyEnum;
  licens: number;
  tillkomstdatum: Date;
  upphovsmanFornamn?: string;
  upphovsmanEfternamn?: string;
  bildfil: File | undefined;
}

export interface RitningPostFormValues {
  titel: string;
  motivbeskrivning: string;
  vy: RitningVyEnum;
  licens: number;
  licensinformation: string;
  tillkomstdatum: Date;
  bildfil: File | undefined;
  godkand_spridning: boolean;
  ritningstyp: RitningstypEnum;
}
